import React, { useEffect, useState, useRef } from 'react'
import { SpeechSynthesisUtterance, SpeechSynthesisPonyfillType } from '@davi-ai/web-speech-cognitive-services-davi'

import { useLocaleStore } from '../Contexts/LocaleContext'
import { useActivityStore } from '../Contexts/ActivityContext'
import { useViewStore } from '../Contexts/ViewContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useDirectlineStore } from '../Contexts/DirectlineContext'
import { useTelemetry } from '../Contexts/TelemetryContext'

import type { CreateUtteranceParams } from '../../models/speechTypes'
import type { RetorikActivity } from '../../models/activityTypes'
import { Mode, RecognitionState, Routes, TraceType } from '../../models/enums'
import { selectVoice } from '../../utils/selectVoice'
import { checkSayAsWordsAndCreateUtterance } from '../../utils/speechSynthesisUtils'

import RetorikSpeech from './RetorikSpeech'

interface SynthesisManagerProps {
  ponyfill: SpeechSynthesisPonyfillType
  isRetorikNews?: boolean
}

const SynthesisManager = ({ ponyfill, isRetorikNews }: SynthesisManagerProps): JSX.Element | null => {
  const cancel = useSpeechStore((state) => state.cancel)
  const streamingQueue = useSpeechStore((state) => state.streamingQueue)
  const voice = useSpeechStore((state) => state.voice)
  const customVoice = useSpeechStore((state) => state.customVoice)
  const activeRecognitionState = useSpeechStore((state) => state.activeRecognitionState)
  const externalTextToSpeech = useSpeechStore((state) => state.externalTextToSpeech)
  const isMicrophoneAllowed = useSpeechStore((state) => state.isMicrophoneAllowed)
  const mode = useSpeechStore((state) => state.mode)
  const route = useViewStore((state) => state.route)
  const locale = useLocaleStore((state) => state.locale)
  const agentData = useRetorikStore((state) => state.agentData)
  const appAvailable = useRetorikStore((state) => state.appAvailable)
  const preventExpectedInputHint = useRetorikStore((state) => state.configuration.preventExpectedInputHint)
  const speechSynthesisOptions = useRetorikStore((state) => state.configuration.speechSynthesisOptions)
  const checkReplacementLocale = useLocaleStore((state) => state.actions.checkReplacementLocale)
  const setRetorikNewsEnded = useUtilsStore((state) => state.actions.setRetorikNewsEnded)
  const { setCurrentOrLastPlayedActivity, resetCancelCurrentUtterance, setSpeaking, setBoundaryData, removeFirstFromStreamingQueue, killCurrentStreaming, askPermission, toggleMicrophone } =
    useSpeechStore((state) => state.actions)
  const addEndedActivity = useActivityStore((state) => state.actions.addEndedActivity)
  const dispatchMarkActivity = useDirectlineStore((state) => state.actions.dispatchMarkActivity)
  const sendTrace = useTelemetry().sendTrace

  const [utterance, setUtterance] = useState<SpeechSynthesisUtterance | null>(null)
  const [nextUtterance, setNextUtterance] = useState<SpeechSynthesisUtterance | null>(null)

  const isNewsViewRef = useRef<boolean>(!!isRetorikNews)
  const lastListTextPlayedRef = useRef<string>('')
  const streamingQueueRef = useRef<Array<RetorikActivity>>([])
  const currentActivityRef = useRef<RetorikActivity | null>(null)

  /**
   * Check activity locale in case of voice change during the speech, to keep the language defined in activity
   * instead of switching languages between answers that are written in the same language
   * @param {string} activityLocale
   * @returns {SpeechSynthesisVoice | null}
   */
  const getVoiceAfterCheckingActivityLocale = (activityLocale: string): SpeechSynthesisVoice | null => {
    return activityLocale === locale ? voice : selectVoice(ponyfill.speechSynthesis.getVoices(), activityLocale, customVoice, agentData)
  }

  /**
   * Check if the activity is a list one, to prevent text re-reading on state update
   * @param {CreateUtteranceParams} params
   * @returns {SpeechSynthesisUtterance}
   */
  const checkActivityAndCreateUtterance = (params: CreateUtteranceParams): SpeechSynthesisUtterance => {
    if (params.activity.attachmentLayout && params.activity.attachmentLayout.toLowerCase() === 'davilist') {
      const textToSpeak = params.activity.speak || params.activity.text
      if (textToSpeak) {
        if (textToSpeak === lastListTextPlayedRef.current) {
          return checkSayAsWordsAndCreateUtterance({ ...params, voice: null }, speechSynthesisOptions?.sayAs)
        } else {
          lastListTextPlayedRef.current = textToSpeak
        }
      }
    }

    return checkSayAsWordsAndCreateUtterance(params, speechSynthesisOptions?.sayAs)
  }

  const resetData = (): void => {
    // Set all activities currently in the queue as ended
    streamingQueueRef.current?.forEach((activity) => {
      addEndedActivity(activity.id)
    })
    streamingQueueRef.current = []
    killCurrentStreaming()

    currentActivityRef.current = null
    setUtterance(null)
    setNextUtterance(null)
    resetCancelCurrentUtterance()
    setSpeaking(false)
    setBoundaryData([])
  }

  const checkReplacementLocaleAndGetUtterance = (activity: RetorikActivity, checkReplacement: boolean = true): SpeechSynthesisUtterance => {
    const replacementLocale = checkReplacement ? checkReplacementLocale(activity.locale || locale) : locale
    const params: CreateUtteranceParams = {
      activity: activity,
      voice: getVoiceAfterCheckingActivityLocale(replacementLocale),
      locale: replacementLocale
    }

    return checkActivityAndCreateUtterance(params)
  }

  /**
   * Check if the microphone can be opened automatically
   */
  const checkDictateState = (): void => {
    if (!preventExpectedInputHint && mode === Mode.vocal && activeRecognitionState === RecognitionState.Closed) {
      if (!isMicrophoneAllowed) {
        askPermission()
      } else {
        toggleMicrophone(true)
      }
    }
  }

  /**
   * On route change, reset all data and check if we are on the news view
   */
  useEffect(() => {
    resetData()
    if (!isRetorikNews) isNewsViewRef.current = route === Routes.News
  }, [route])

  /**
   * On cancel call, reset all data and kill current streaming
   */
  useEffect(() => {
    cancel && resetData()
  }, [cancel])

  /**
   * Play external speech immediately but don't add it to the history
   */
  useEffect(() => {
    if (externalTextToSpeech) {
      const activity = {
        speak: externalTextToSpeech,
        locale: locale
      } as RetorikActivity
      currentActivityRef.current = activity
      setUtterance(checkReplacementLocaleAndGetUtterance(activity, false))
    }
  }, [externalTextToSpeech])

  const dispatchSpeechEvent = (start: boolean): void => {
    const speechEvent = start ? new Event('retorikSpeakStart') : new Event('retorikSpeakStop')
    document.dispatchEvent(speechEvent)
  }

  /**
   * Called when utterance's "start" event is fired
   */
  const handleUtteranceStart = (): void => {
    sendTrace(TraceType.utteranceStart, {
      activityId: currentActivityRef.current?.id,
      text: currentActivityRef.current?.speak || currentActivityRef.current?.text
    })
    setSpeaking(true)

    if (!isNewsViewRef.current && currentActivityRef?.current) {
      setCurrentOrLastPlayedActivity(currentActivityRef.current)
      currentActivityRef.current.id && dispatchMarkActivity(currentActivityRef.current.id, true)
    }
    dispatchSpeechEvent(true)

    // Set next utterance to prepare synthesis data in order to improve fluidity
    if (streamingQueueRef.current.length && !streamingQueueRef.current[0].value?.streamingCount) {
      const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[0], false)
      setNextUtterance((current) => (utt.text === current?.text ? null : utt))
    } else {
      setNextUtterance(null)
    }
  }

  /**
   * Called when utterance's "end" event is fired
   */
  const handleUtteranceEnd = (): void => {
    sendTrace(TraceType.utteranceEnd, {
      activityId: currentActivityRef.current?.id
    })
    setSpeaking(false)
    dispatchSpeechEvent(false)

    if (isNewsViewRef.current) {
      setRetorikNewsEnded(true)
      currentActivityRef.current = null
    } else {
      handleEnded()
    }
  }

  const handleEnded = (): void => {
    // Add played activity id to context's endedActivities
    if (currentActivityRef.current?.id) {
      addEndedActivity(currentActivityRef.current.id)
      dispatchMarkActivity(currentActivityRef.current.id, false)
    }

    if (streamingQueueRef.current?.length) {
      // Check if this is the message sent after streaming activities
      if (streamingQueueRef.current[0].value?.streamingCount) {
        addEndedActivity(streamingQueueRef.current[0].id)
        // If there is another activity after this message, play it. If not, let's check if the microphone should be opened
        if (streamingQueueRef.current?.length > 1) {
          const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[1])
          currentActivityRef.current = streamingQueueRef.current[1]
          setUtterance(utt)
          removeFirstFromStreamingQueue(true)
        } else {
          streamingQueueRef.current[0].inputHint === 'expectingInput' && checkDictateState()
          currentActivityRef.current = null
          setUtterance(null)
          setNextUtterance(null)
          removeFirstFromStreamingQueue()
        }
      } else {
        const utt = checkReplacementLocaleAndGetUtterance(streamingQueueRef.current[0])
        currentActivityRef.current = streamingQueueRef.current[0]
        setUtterance(utt)
        removeFirstFromStreamingQueue()
      }
    } else {
      currentActivityRef.current?.inputHint === 'expectingInput' && checkDictateState()
      currentActivityRef.current = null
      setUtterance(null)
      setNextUtterance(null)
    }
  }

  /**
   * Wait for app loading and voice selection to begin playing speech
   */
  useEffect(() => {
    // Set ref from the state to use in events
    streamingQueueRef.current = streamingQueue

    if (appAvailable && voice && streamingQueue.length && !currentActivityRef.current) {
      handleEnded()
    }
  }, [appAvailable, voice, streamingQueue])

  return (
    <RetorikSpeech
      ponyfill={ponyfill}
      appAvailable={appAvailable}
      utterance={utterance}
      nextUtterance={nextUtterance}
      onStart={handleUtteranceStart}
      onError={handleUtteranceEnd}
      onEnd={handleUtteranceEnd}
    />
  )
}

export default SynthesisManager
